<template>
    <BaseButton v-if="opensDialog || items.length === 1" theme="primary" :size="compact ? 'sm' : 'md'" class="base-btn-with-icon base-btn-icon-start" @click="handleButtonClick">
        <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
        {{ opensDialog ? t("connections.addPeople.trigger") : items[0].label }}
    </BaseButton>
    <BaseDropdownMenu v-else :sections="sections" @select="handleSelect" @update:open="$event && logButtonClicked()">
        <template #trigger>
            <BaseButton theme="primary" :size="compact ? 'sm' : 'md'" class="base-btn-with-icon base-btn-icon-start">
                <i class="bi bi-plus-lg icon-semibold" aria-hidden="true"></i>
                {{ t("connections.addPeople.trigger") }}
                <i class="bi bi-chevron-down text-sm/none" aria-hidden="true"></i>
            </BaseButton>
        </template>
    </BaseDropdownMenu>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import { COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS } from "~vue/components/navigation/menuPanes";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

type AddPeopleMenuProps = {
    compact?: boolean;
    opensDialog?: boolean;
    publicProfileEnabled?: boolean;
    calendarImportEnabled?: boolean;
    hasCalendarIntegration?: boolean;
    csvImportEnabled?: boolean;
};

const {
    compact = false,
    opensDialog = false,
    publicProfileEnabled = false,
    calendarImportEnabled = false,
    hasCalendarIntegration = false,
    csvImportEnabled = false,
} = defineProps<AddPeopleMenuProps>();

const { t } = useI18n();

const emit = defineEmits<(event: "organization" | "calendar" | "connectCalendar" | "csv" | "manual" | "open") => void>();

type AddPeopleItem = DropdownMenuItem & { id: Parameters<typeof emit>[0] };

const items = computed<AddPeopleItem[]>(() => {
    const entries: AddPeopleItem[] = [];
    if (publicProfileEnabled) {
        entries.push({ id: "organization", type: "button", label: t("connections.addPeople.fromOrganization"), icon: `bi ${COLLABORATION_PROFILE_BOOTSTRAP_ICON_CLASS}` });
    }
    if (calendarImportEnabled) {
        entries.push(
            hasCalendarIntegration
                ? { id: "calendar", type: "button", label: t("connections.addPeople.fromCalendar"), icon: "bi bi-calendar3" }
                : { id: "connectCalendar", type: "button", label: t("connections.addPeople.connectCalendar"), icon: "bi bi-calendar3" },
        );
    }
    if (csvImportEnabled) {
        entries.push({ id: "csv", type: "button", label: t("connections.addPeople.uploadCsv"), icon: "bi bi-file-earmark-arrow-up" });
    }
    entries.push({ id: "manual", type: "button", label: t("connections.addPeople.addOneAtATime"), icon: "bi bi-person-plus" });
    return entries;
});

const sections = computed<DropdownMenuSection[]>(() => [{ id: "add-people", items: items.value }]);

const logButtonClicked = () => logUserInteraction(AmplitudeEventNames.PEOPLE_ADD_BUTTON_CLICKED);

const handleSelect = ({ id }: DropdownMenuItem) => emit(id as Parameters<typeof emit>[0]);

function handleButtonClick() {
    logButtonClicked();
    if (opensDialog) {
        emit("open");
    } else {
        handleSelect(items.value[0]);
    }
}
</script>
